import { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback } from "react";
import api, { lastApiActivityAt } from "../lib/api";
import { clearAllDateEditKeys } from "../lib/dateEditPersistence";
import { setOpenTasksScope, scopeFromUser } from "../lib/openTasks";
import { fijarUsuarioPrecarga } from "../lib/notas";

const AuthContext = createContext(null);

// Minutos antes del cierre por inactividad en los que se muestra el aviso
// (pedido del Founder 2026-10-15): solo admins/founder/business_owner reciben
// `idle_timeout_min` desde /auth/me — el resto de roles no tiene este límite.
const IDLE_WARNING_MINUTES_BEFORE = 5;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idleWarning, setIdleWarning] = useState(false);

  // La caché compartida de «Clientes por contactar» se ata al usuario+equipo autenticado; cualquier cambio la descarta.
  useLayoutEffect(() => { setOpenTasksScope(scopeFromUser(user)); }, [user]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle Google OAuth callback on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (sessionId) {
      // Process Google OAuth callback
      api.post("/auth/google-callback", { session_id: sessionId })
        .then(({ data }) => {
          setUser(data);
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
        })
        .catch(() => {
          setUser(false);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      checkAuth();
    }
  }, [checkAuth]);

  // Proactive silent refresh — every 30 minutes, hit /auth/refresh so the
  // 24h access cookie gets renewed before it can expire. Keeps mobile users
  // (iPhone Safari especially) signed in indefinitely while the tab/PWA is
  // open. Pauses while the tab is hidden to avoid waking the device.
  useEffect(() => {
    if (!user) return;
    let stopped = false;
    const refreshNow = () => {
      if (document.visibilityState === "hidden") return;
      api.post("/auth/refresh").catch(() => {});
    };
    const id = setInterval(() => {
      if (!stopped) refreshNow();
    }, 30 * 60 * 1000); // 30 minutes
    // Also refresh immediately when the user returns to the tab so a long
    // backgrounded PWA gets a fresh cookie on resume.
    const onVis = () => {
      if (document.visibilityState === "visible") refreshNow();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stopped = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user]);

  // Aviso previo de cierre por inactividad (pedido Founder 2026-10-15,
  // Bloque 3): el servidor cierra la sesión si pasan `idle_timeout_min`
  // minutos sin ninguna llamada real a la API (no cuenta mover el mouse).
  // Revisamos cada 30s contra `lastApiActivityAt` y avisamos cuando falten
  // pocos minutos, para que el operador decida si sigue trabajando.
  useEffect(() => {
    const limitMin = user && user.idle_timeout_min;
    if (!limitMin) {
      setIdleWarning(false);
      return;
    }
    const check = () => {
      const idleMinutes = (Date.now() - lastApiActivityAt) / 60000;
      const remaining = limitMin - idleMinutes;
      setIdleWarning(remaining > 0 && remaining <= IDLE_WARNING_MINUTES_BEFORE);
    };
    check();
    const id = setInterval(check, 30 * 1000);
    return () => clearInterval(id);
  }, [user]);

  // Botón "Seguir trabajando" del aviso. OJO: /auth/me está EXCLUIDO del
  // chequeo de inactividad en el backend (no cuenta como actividad), así
  // que usamos un endpoint normal y liviano para que sí reinicie el reloj
  // real del servidor.
  const dismissIdleWarning = useCallback(() => {
    setIdleWarning(false);
    api.get("/notifications/count").catch(() => {});
  }, []);

  const login = async (email, password, verificationCode) => {
    const { data } = await api.post("/auth/login", { email, password, verification_code: verificationCode });
    if (data.role) {
      setUser(data);
    }
    return data;
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(false);
    try { fijarUsuarioPrecarga(""); } catch { /* sin almacenamiento */ }   // la entrega Notas->teléfono no sobrevive al cierre de sesión
    // Drop any persisted manual date edits from EnviarPage. Selective instead
    // of a full sessionStorage.clear() so other unrelated sessionStorage
    // entries (e.g. license_expired_msg) are left untouched.
    try { clearAllDateEditKeys(); } catch { /* sessionStorage unavailable */ }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, setUserDirect: setUser }}>
      {children}
      {idleWarning && (
        <div
          data-testid="idle-session-warning"
          style={{
            position: "fixed", bottom: 20, right: 20, zIndex: 9999,
            background: "#1f2937", color: "#fff", padding: "16px 20px",
            borderRadius: 10, boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
            maxWidth: 360, border: "1px solid #f59e0b",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            Tu sesión se cerrará pronto por inactividad
          </div>
          <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 10 }}>
            No se detectó actividad reciente. Si no haces nada, el sistema
            cerrará tu sesión por seguridad en unos minutos.
          </div>
          <button
            type="button"
            onClick={dismissIdleWarning}
            style={{
              background: "#f59e0b", color: "#1f2937", border: "none",
              borderRadius: 6, padding: "6px 14px", fontWeight: 600, cursor: "pointer",
            }}
          >
            Seguir trabajando
          </button>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be within AuthProvider");
  return ctx;
}
